import cn from "classnames";
import { SelectedRoute, SuggestedRoute, RouteMetadata } from "module/eddm/dto";
import { formatNumber, toFixed2 } from "utils/formators";
import { usePostHog } from "posthog-js/react";
import { EmitAnalyticsEvent } from "module/analytics/application/EmitAnalyticsEvent";
import { GoogleMap } from "@react-google-maps/api";
import { RouteIcon } from "components/icons/components/RouteIcon";
import { DeleteIcon } from "static/media/delete-icon";
import { Check } from "lucide-react";
import { ResidencesIcon } from "components/icons/components/ResidencesIcon";
import { MAP_TYPES } from "pages/constants";
import {
  updateSuggestedRoutesOnDataLayer,
  handleHoverMultipleSelectedRoutes,
  handleHoverSelectedMissionUpdate,
} from "components/audience-map/utils";
import { getOverlayPath } from "Hooks/useOverlaysFetch";
import { SuggestionStart } from "static/media";
import { useStore } from "store";
import { SuggestedRouteCard } from "./components/index";

interface SuggestionAddressCardProps {
  suggestedRoutes: SuggestedRoute[];
  setSelectedRoutes: (selectedRoutes: SelectedRoute[] | ((selectedRoutes: SelectedRoute[]) => SelectedRoute[])) => void;
  googleMap: GoogleMap | null;
  setShouldAutoSave: (shouldAutoSave: boolean) => void;
  setIsSingleRouteSelection: (isSingleSelection: boolean) => void;
  setIsUndoOrRedo: (isUndoOrRedo: boolean) => void;
}

export const SuggestedRoutesBlock = ({
  suggestedRoutes,
  setSelectedRoutes,
  googleMap,
  setShouldAutoSave,
  setIsSingleRouteSelection,
  setIsUndoOrRedo,
}: SuggestionAddressCardProps) => {
  const {
    user,
    client: { name: clientName },
    campaign: { id: campaignId },
    costsCalculationData: { costPerFlyer },
    setSuggestedRoutes,
    setSuggestionsEnabled,
    suggestionsEnabled,
    dismissedSuggestedRoutes,
    setDismissedSuggestedRoutes,
  } = useStore();
  const posthog = usePostHog();
  const posthogEventTracker = new EmitAnalyticsEvent(posthog);

  const totalSuggestedAddresses = suggestedRoutes.reduce((acc, suggestedRoute) => {
    acc += suggestedRoute.properties.APT_COUNT + suggestedRoute.properties.HOME_COUNT;
    return acc;
  }, 0);

  const handleRouteDelete = (route: SuggestedRoute) => {
    if (!route || !googleMap) return;
    const updatedRoutes = suggestedRoutes.filter((suggestedRoute) => suggestedRoute._id !== route._id);
    let newSuggestionsEnabled = suggestionsEnabled;
    posthogEventTracker.run({
      eventName: "Dismissed one suggested route",
      userId: user?.id,
      clientName,
      device: navigator.userAgent,
      additionalPayload: {
        campaignId,
        fitScore: `${route.suitabilityScore}%`,
      },
    });
    if (!dismissedSuggestedRoutes.find((dismissedRoute) => dismissedRoute._id === route._id)) {
      setDismissedSuggestedRoutes([...dismissedSuggestedRoutes, route]);
    }
    if (!updatedRoutes.length) {
      newSuggestionsEnabled = false;
      setSuggestionsEnabled(newSuggestionsEnabled);
    }
    // @ts-ignore
    updateSuggestedRoutesOnDataLayer({
      // @ts-ignore
      dataLayer: googleMap.data,
      // @ts-ignore
      suggestedRoutes: updatedRoutes,
      suggestionsEnabled: newSuggestionsEnabled,
    });
    setSuggestedRoutes(updatedRoutes);
  };

  const handleSelectSuggestedRoutes = () => {
    setSuggestionsEnabled(false);
    setIsUndoOrRedo(false);
    setShouldAutoSave(true);
    posthogEventTracker.run({
      eventName: "Selected all suggested routes",
      userId: user?.id,
      clientName,
      device: navigator.userAgent,
      additionalPayload: {
        campaignId,
        fitScores: suggestedRoutes.map((suggestedRoute) => `${suggestedRoute.suitabilityScore}%`).join(", "),
      },
    });
    //@ts-ignore
    googleMap?.data.forEach((feature) => {
      const isMission = feature.getProperty("type") === "mission";
      const featureId = feature.getProperty("id");
      const isSuggestedFeature = suggestedRoutes.find((suggestedRoute) => suggestedRoute._id === featureId);
      if (!!isSuggestedFeature && isMission) {
        feature.setProperty("selected", true);
        feature.setProperty("suggested", false);
      }
    });
    setSelectedRoutes((routes: SelectedRoute[]): SelectedRoute[] => {
      const updatedRoutes: SelectedRoute[] = [...routes];
      suggestedRoutes.forEach((route: SuggestedRoute) => {
        if (!updatedRoutes.find((r: SelectedRoute) => r.id === route._id)) {
          updatedRoutes.push({
            id: route._id,
            paths: getOverlayPath({
              coords: route.geometry.coordinates,
              isMultiPolygon: route.geometry.type == "MultiPolygon",
            }),
            type: MAP_TYPES.POLYGON,
            metadata: route.properties as RouteMetadata,
          });
        }
      });
      return updatedRoutes;
    });
  };

  const handleRouteSelect = (route: SuggestedRoute) => {
    if (!route || !googleMap) return;
    setIsSingleRouteSelection(true);
    setIsUndoOrRedo(false);
    setShouldAutoSave(true);
    //@ts-ignore
    googleMap?.data.forEach((feature) => {
      const featureId = feature.getProperty("id");
      if (featureId === route._id) {
        feature.setProperty("selected", true);
        feature.setProperty("suggested", false);
      }
    });
    posthogEventTracker.run({
      eventName: "Selected one suggested route",
      userId: user?.id,
      clientName,
      device: navigator.userAgent,
      additionalPayload: {
        campaignId,
        fitScore: `${route.suitabilityScore}%`,
      },
    });
    setSelectedRoutes((routes: SelectedRoute[]): SelectedRoute[] => {
      const updatedRoutes: SelectedRoute[] = [...routes];
      if (!updatedRoutes.find((r: SelectedRoute) => r.id === route._id)) {
        updatedRoutes.push({
          id: route._id,
          paths: getOverlayPath({
            coords: route.geometry.coordinates,
            isMultiPolygon: route.geometry.type == "MultiPolygon",
          }),
          type: MAP_TYPES.POLYGON,
          metadata: route.properties as RouteMetadata,
        });
      }
      return updatedRoutes;
    });
  };

  const handleDismissAllSuggestedRoutes = () => {
    setDismissedSuggestedRoutes([...dismissedSuggestedRoutes, ...suggestedRoutes]);
    posthogEventTracker.run({
      eventName: "Dismissed all suggested routes",
      userId: user?.id,
      clientName,
      device: navigator.userAgent,
      additionalPayload: {
        campaignId,
        fitScores: suggestedRoutes.map((suggestedRoute) => `${suggestedRoute.suitabilityScore}%`).join(", "),
      },
    });
    //@ts-ignore
    googleMap?.data.forEach((feature) => {
      const isMission = feature.getProperty("type") === "mission";
      const isSuggested = feature.getProperty("suggested");
      if (isSuggested && isMission) {
        feature.setProperty("suggested", false);
      }
    });
    setSuggestedRoutes([]);
    setSuggestionsEnabled(false);
  };

  return (
    <div className="cb-v2-root relative mx-2 flex flex-col items-center justify-center rounded-[8px] bg-[#F2F2FE] px-[17px] py-[10px] dark:bg-popover">
      <span className="absolute left-[45%] top-[-3%] h-0 w-0 border-x-[20px] border-b-[20px] border-x-transparent border-b-[#F2F2FE] dark:border-b-popover"></span>
      {suggestedRoutes.length && (
        <div className="flex w-full items-center justify-between border-b border-solid border-border py-[10px]">
          <button
            className="flex h-[26px] w-[86px] cursor-pointer items-center justify-center rounded-[4px] border border-solid border-border bg-transparent text-[10px] font-bold text-foreground [&_svg]:ml-[5px]"
            onClick={handleDismissAllSuggestedRoutes}
          >
            Dismiss all
            <DeleteIcon disabled={false} width={"16"} height={"16"} stroke="currentColor" />
          </button>
          <button
            className="relative flex h-[22px] w-[205px] cursor-pointer items-center justify-center rounded-[4px] border border-solid border-primary bg-primary text-[10px] font-bold text-primary-foreground [&_svg]:ml-[5px]"
            onClick={handleSelectSuggestedRoutes}
          >
            Add all
            <Check className="absolute right-[5px] h-4 w-4" />
          </button>
        </div>
      )}
      <div className="mb-[5px] flex min-h-[30px] w-full items-center justify-between">
        <div
          className="group relative flex w-fit cursor-default items-center overflow-hidden py-1 pl-[2px] pr-[5px] font-bold before:absolute before:inset-0 before:z-0 before:-translate-x-[101%] before:bg-[#4F5B94] before:transition-transform before:duration-[400ms] before:ease-out before:content-['']  hover:before:translate-x-0"
          onMouseOver={() => {
            handleHoverMultipleSelectedRoutes({
              mapInit: googleMap,
              selectedRoutes: suggestedRoutes.map((route) => ({ ...route, id: route._id })),
            });
          }}
          onMouseLeave={() => handleHoverSelectedMissionUpdate({ mapInit: googleMap, hoveredMission: null })}
        >
          <SuggestionStart stroke="#EE4360" className="relative z-[1]" />
          <span className="relative z-[1] ml-[5px] overflow-hidden text-ellipsis whitespace-nowrap text-[14px] font-normal text-[#222336] transition-colors duration-[400ms] group-hover:text-white dark:text-foreground">
            Suggested routes
          </span>
        </div>
      </div>
      <div
        className={cn(
          "relative flex w-full items-center justify-between border border-solid text-[14px]",
          !suggestedRoutes?.length
            ? "rounded-[8px] border-[#F0F0FF] bg-[#FCFCFF] p-[12px] dark:border-border dark:bg-card"
            : "border-transparent pb-[12px]"
        )}
      >
        <div className="flex flex-col">
          <span className="text-[12px] text-muted-foreground">Routes</span>
          <span className="flex items-center text-[14px] font-medium text-[#222336] dark:text-foreground [&_svg]:mr-[2px]">
            <RouteIcon stroke="currentColor" /> {suggestedRoutes?.length || 0}
          </span>
        </div>
        <div className="flex flex-col">
          <span className="text-[12px] text-muted-foreground">Residences</span>
          <span className="flex items-center text-[14px] font-medium text-[#222336] dark:text-foreground [&_svg]:mr-[2px]">
            <ResidencesIcon stroke="currentColor" />
            {formatNumber(totalSuggestedAddresses)}
          </span>
        </div>
        <div className="flex flex-col items-end">
          <span className="text-[12px] text-muted-foreground">Costs</span>
          <span className="font-bold text-[#222336] dark:text-foreground">
            $ {formatNumber(toFixed2(costPerFlyer * totalSuggestedAddresses))}
          </span>
        </div>
      </div>
      {!!suggestedRoutes?.length &&
        suggestedRoutes.map((route, i) => (
          <SuggestedRouteCard
            key={`${route.properties.ZIP}-${route.properties.CROUTE}`}
            route={route}
            className={cn({
              "rounded-t-[8px]": i === 0,
              "rounded-b-[8px]": i === suggestedRoutes.length - 1,
            })}
            handleRouteDelete={handleRouteDelete}
            handleRouteSelect={handleRouteSelect}
            googleMap={googleMap}
          />
        ))}
    </div>
  );
};
